// Slice-10 login screen — multi-IdP discovery.
//
// Two-step flow (Plan §10.1):
//   1. User types tenant → SPA calls /v1/auth/discover.
//   2a. If the tenant federates (authorize_url present), we redirect to
//       the IdP. The IdP redirects back to /login with ?code=... which
//       the slice-11 frontend extension picks up + POSTs to /auth/exchange.
//   2b. If local_login_enabled, the slice-12 SPA closeout adds a
//       password form here. For slice 10 we only support the federated
//       redirect path; local login is captured behind a feature flag.

import { useState, type FormEvent } from "react";
import { useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { apiClient } from "../api/client";
import { useI18n } from "../i18n/I18nContext";

const tenantSchema = z.object({
  tenant: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/u, "tenant identifier must be lowercase alphanumeric + dash"),
});

type TenantForm = z.infer<typeof tenantSchema>;

export function LoginScreen(): JSX.Element {
  const { t } = useI18n();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<TenantForm>({
    defaultValues: { tenant: "demo" },
  });

  const discover = useMutation({
    mutationFn: async (tenant: string) => {
      const response = await apiClient.GET("/auth/discover", {
        params: { query: { tenant } },
      });
      if (!response.data) {
        throw new Error(response.error?.detail ?? "discover_failed");
      }
      return response.data;
    },
    onSuccess: (discovery) => {
      // Federated path: bounce to the IdP. The slice-11 frontend pass
      // handles the callback that exchanges code → access token.
      if (discovery.authorize_url) {
        window.location.assign(discovery.authorize_url);
        return;
      }
      // No federated path: stay on /login and surface a message. Slice
      // 12 adds the local-IdP form behind a flag.
      setError(t("login.redirect"));
    },
    onError: () => {
      setError(t("login.error.unknown_tenant"));
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    void form.handleSubmit(async ({ tenant }) => {
      const parsed = tenantSchema.safeParse({ tenant });
      if (!parsed.success) {
        setError(t("login.error.unknown_tenant"));
        return;
      }
      await discover.mutateAsync(parsed.data.tenant);
      // Slice 12 extends this with the actual /auth/exchange step. For
      // slice 10 a successful discover transitions the SPA to a "go to
      // your IdP" affordance; once the IdP returns the code, the
      // callback route on /login (added in slice 11) wires the rest.
      void router.invalidate();
    })(event);
  };

  return (
    <main className="rc-page">
      <div className="rc-card" style={{ maxWidth: 420, margin: "10vh auto" }}>
        <h1 style={{ marginTop: 0 }}>{t("login.title")}</h1>
        <form onSubmit={onSubmit}>
          <label htmlFor="tenant" style={{ display: "block", marginBottom: 6 }}>
            {t("login.tenant.label")}
          </label>
          <input
            id="tenant"
            className="rc-input"
            placeholder={t("login.tenant.placeholder")}
            autoComplete="organization"
            disabled={discover.isPending}
            {...form.register("tenant")}
          />
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              className="rc-button rc-button--primary"
              disabled={discover.isPending}
            >
              {discover.isPending ? t("login.discovering") : t("login.continue")}
            </button>
          </div>
          {error && (
            <p role="alert" style={{ color: "var(--rc-color-danger)", marginTop: 12 }}>
              {error}
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
